import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Container,
  Typography,
  MenuItem,
  Grid,
  Paper,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import config from '../config';
import empresasApi from '../api/empresasApi';

function MiEmpresaForm() {
  const [empresa, setEmpresa] = useState({
    nombre_empresa: '',
    nro_doc_empresa: '',
    contacto_empresa: '',
    telefono_empresa: '',
    email_empresa: '',
    id_pais: '',
  });
  const [paises, setPaises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([fetchPaises(), fetchEmpresa()]).then(() => {
      setLoading(false);
    }).catch(error => {
      setError('Error al cargar los datos. Por favor, inténtelo de nuevo más tarde.');
      setLoading(false);
    });
  }, []);

  const fetchPaises = async () => {
    try {
      // TODO: Crear paisesApi cuando se migre a V1
      const response = await axios.get(`${config.API_BASE_URL}/api/paises`);
      setPaises(response.data);
    } catch (error) {
      console.error('Error al obtener los países:', error);
      throw error;
    }
  };

  const fetchEmpresa = async () => {
    try {
      const response = await empresasApi.obtenerMiEmpresa();
      setEmpresa(response.data);
    } catch (error) {
      console.error('Error al obtener los datos de la empresa:', error);
      throw error;
    }
  };

  const handleChange = (e) => {
    setEmpresa({ ...empresa, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);

      await empresasApi.actualizarMiEmpresa(empresa);

      setSuccessMessage('Los datos de la empresa se han actualizado correctamente');
      
      // Redirigir después de 2 segundos
      setTimeout(() => {
        navigate('/home/miempresa');
      }, 2000);
    } catch (error) {
      console.error('Error al actualizar la empresa:', error);
      setError('No se pudieron guardar los cambios. Por favor, inténtelo de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Cargando información...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Editar información de mi empresa
          </Typography>
          <Button 
            variant="outlined" 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate('/home/miempresa')}
          >
            Volver
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Nombre de la Empresa"
                variant="outlined"
                fullWidth
                name="nombre_empresa"
                value={empresa.nombre_empresa || ''}
                onChange={handleChange}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Número de Documento"
                variant="outlined"
                fullWidth
                name="nro_doc_empresa"
                value={empresa.nro_doc_empresa || ''}
                onChange={handleChange}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="País"
                variant="outlined"
                fullWidth
                name="id_pais"
                value={empresa.id_pais || ''}
                onChange={handleChange}
                required
              >
                {paises.map((pais) => (
                  <MenuItem key={pais.id_pais} value={pais.id_pais}>
                    {pais.det_pais} ({pais.simbolo_moneda})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Contacto"
                variant="outlined"
                fullWidth
                name="contacto_empresa"
                value={empresa.contacto_empresa || ''}
                onChange={handleChange}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Teléfono"
                variant="outlined"
                fullWidth
                name="telefono_empresa"
                value={empresa.telefono_empresa || ''}
                onChange={handleChange}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Email"
                variant="outlined"
                fullWidth
                name="email_empresa"
                value={empresa.email_empresa || ''}
                onChange={handleChange}
                required
                type="email"
              />
            </Grid>
            
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Button 
                variant="contained" 
                color="primary" 
                type="submit" 
                size="large"
                fullWidth
                startIcon={<SaveIcon />}
                disabled={saving}
              >
                {saving ? 'Guardando...' : 'Guardar cambios'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
}

export default MiEmpresaForm;